import * as turf from "@turf/turf";
import { useEffect } from "react";
import { Layer, MapLayerMouseEvent, Source, useMap } from "react-map-gl/maplibre";
import { useGeoAudienceEditor } from "shared/components/message/compose/geoAudience/useGeoAudienceEditor";

type SenderGeometry = GeoJSON.Polygon | GeoJSON.MultiPolygon;
type SenderFeature = GeoJSON.Feature<SenderGeometry>;

function buildSenderBoundaryFeatures(features: SenderFeature[]) {
    if (features.length === 0) return [];

    const unioned =
        features.length === 1
            ? features[0]
            : turf.union({
                  type: "FeatureCollection",
                  features,
              })!;

    const { geometry } = unioned;
    const strippedGeometry =
        geometry.type === "Polygon"
            ? {
                  ...geometry,
                  coordinates: geometry.coordinates.length > 0 ? [geometry.coordinates[0]] : [],
              }
            : {
                  ...geometry,
                  coordinates: geometry.coordinates.map((polygon) => (polygon.length > 0 ? [polygon[0]] : [])),
              };
    return [{ ...unioned, geometry: strippedGeometry }];
}

function getClickFeatureId(event: MapLayerMouseEvent, mapRef: ReturnType<typeof useMap>["current"]) {
    if (!mapRef) return null;

    const layers = ["sender-boundary-interactive-layer", "sender-boundary-selected-layer"].filter((id) => mapRef.getLayer(id));
    if (layers.length === 0) return null;

    const features = mapRef.queryRenderedFeatures(event.point, { layers });
    const [smallest] = [...features].sort((a, b) => turf.area(a) - turf.area(b)); // get smallest for selecting nested feature
    const id = smallest?.properties?.["id"];

    if (id === null || id === undefined) return null;
    return String(id);
}

function buildDrawModeBoundaries(
    tab: string,
    senderGeoFilters: { id: string; boundary: string }[],
    senderFilters: { id: string; layer?: { id: string } }[],
) {
    if (tab !== "Draw") return [];

    const idToGeometry = new Map<string, SenderGeometry>();
    senderGeoFilters.forEach((gf) => idToGeometry.set(gf.id, JSON.parse(gf.boundary) as SenderGeometry));

    const features = senderFilters
        .map((f) => {
            const geometry = idToGeometry.get(f.id);
            if (!geometry) return null;
            const feature: SenderFeature = {
                type: "Feature",
                geometry,
                properties: {},
            };
            return feature;
        })
        .filter((feature) => feature !== null);

    return buildSenderBoundaryFeatures(features);
}

export const GeoAudienceSenderBoundaryOverlay = () => {
    const map = useMap();
    const { tab, selectedLayerId, selectedSenderFilters, handleToggleSenderFilter, senderFilters, senderGeoFilters } =
        useGeoAudienceEditor();

    const filteredFilterIds = new Set(
        senderFilters.filter((filter) => (selectedLayerId ? filter.layer?.id === selectedLayerId : false)).map((f) => f.id),
    );

    const geoFilterFeatures = senderGeoFilters
        .filter((filter) => filteredFilterIds.has(filter.id))
        .map((filter) => {
            const geometry = JSON.parse(filter.boundary);
            const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
                type: "Feature",
                id: filter.id,
                geometry,
                properties: { id: filter.id, name: filter.name, projectedUsersWithin: filter.projectedUsersWithin },
            };
            return feature;
        });
    const interactiveGeoFilterFeatures = tab === "Draw" ? [] : geoFilterFeatures;

    const senderBoundaryFeatures = tab === "Draw" ? [] : buildSenderBoundaryFeatures(geoFilterFeatures);

    const drawModeBoundaries = buildDrawModeBoundaries(tab, senderGeoFilters, senderFilters);

    useEffect(() => {
        if (!map.current) return;

        function handleMapClick(event: MapLayerMouseEvent) {
            const id = getClickFeatureId(event, map.current);
            if (!id || tab === "Draw") return;

            const clickedLayerId = senderFilters.find((filter) => filter.id === id)?.layer?.id ?? null;
            if (!selectedLayerId || clickedLayerId !== selectedLayerId) return;

            const mapFeature = interactiveGeoFilterFeatures.find((feature) => String(feature.id) === id);
            if (mapFeature) {
                handleToggleSenderFilter({
                    id,
                    name: String(mapFeature.properties?.["name"] ?? "Unnamed"),
                    projectedUsersWithin: Number(mapFeature.properties?.["projectedUsersWithin"] ?? 0),
                    geometry: mapFeature.geometry,
                });
                return;
            }

            const selectedMatch = selectedSenderFilters.find((f) => f.id === id);
            if (selectedMatch) {
                handleToggleSenderFilter(selectedMatch);
            }
        }

        map.current.on("click", handleMapClick);
        return () => {
            if (map.current) map.current.off("click", handleMapClick);
        };
    }, [map.current, interactiveGeoFilterFeatures, selectedSenderFilters, tab, selectedLayerId, senderFilters]);

    if (senderBoundaryFeatures.length === 0 && selectedSenderFilters.length === 0 && drawModeBoundaries.length === 0) {
        return null;
    }

    const selectedFeaturesSource =
        tab === "Draw"
            ? []
            : selectedSenderFilters.map((filter) => ({
                  type: "Feature",
                  id: filter.id,
                  geometry: filter.geometry,
                  properties: { id: filter.id, name: filter.name, projectedUsersWithin: filter.projectedUsersWithin },
              }));

    return (
        <>
            {interactiveGeoFilterFeatures.length > 0 && (
                <Source
                    id="sender-boundary-interactive-source"
                    type="geojson"
                    data={{ type: "FeatureCollection", features: interactiveGeoFilterFeatures }}
                >
                    <Layer id="sender-boundary-interactive-layer" type="fill" paint={{ "fill-color": "rgba(0, 0, 0, 0)" }} />
                    <Layer
                        id="sender-boundary-interactive-outline-layer"
                        type="line"
                        paint={{ "line-color": "#868e96", "line-width": 2 }}
                    />
                </Source>
            )}
            {selectedFeaturesSource.length > 0 && (
                <Source
                    id="sender-boundary-selected-source"
                    type="geojson"
                    data={{ type: "FeatureCollection", features: selectedFeaturesSource }}
                >
                    <Layer id="sender-boundary-selected-layer" type="fill" paint={{ "fill-color": "rgba(255, 0, 0, 0.2)" }} />
                </Source>
            )}
            {senderBoundaryFeatures.length > 0 && (
                <Source id="sender-boundary-source" type="geojson" data={{ type: "FeatureCollection", features: senderBoundaryFeatures }}>
                    <Layer id="sender-boundary-layer" type="line" paint={{ "line-color": "#FF0000", "line-width": 2 }} />
                </Source>
            )}
            {drawModeBoundaries.length > 0 && (
                <Source id="sender-boundary-draw-source" type="geojson" data={{ type: "FeatureCollection", features: drawModeBoundaries }}>
                    <Layer id="sender-boundary-draw-layer" type="line" paint={{ "line-color": "#FF0000", "line-width": 2 }} />
                </Source>
            )}
        </>
    );
};
